import {createSelector} from 'reselect';
import {getTranslations, getContactOwnerForm, getTravelerFeatures} from './store';
import {getBrandName} from './system';
import {isInquiryModalOpen} from './modalStates';
import {getHostType} from './listing';
import {getUserData} from './userData';
import {labelMaker} from '../../server/utils/labelMaker';

const getInquiryModalPlatformLabels = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        if (hostType.owner) {
            return {
                question: trans.questionForOwner,
                message: trans.messageToOwner
            };
        }
        return {
            question: trans.questionForManager,
            message: trans.messageToManager
        };
    }
);

/* eslint-disable complexity */
const getContactOwnerFormLabels = createSelector(
    [
        getTranslations,
        getBrandName,
        getContactOwnerForm,
        getInquiryModalPlatformLabels,
        getTravelerFeatures
    ],
    (trans, brandName, contactOwnerForm, inquiryModalPlatformLabels, travelerFeatures) => {
        const {question, message} = inquiryModalPlatformLabels;
        const {invalid, isFormValid, touched} = contactOwnerForm;

        const showFirstNameErrorMsg =
            !isFormValid && touched.has('firstName') && invalid.has('firstName');
        const showLastNameErrorMsg =
            !isFormValid && touched.has('lastName') && invalid.has('lastName');
        const showEmailAddressErrorMsg =
            !isFormValid && touched.has('emailAddress') && invalid.has('emailAddress');
        const showTermsAndConditionsErrorMsg =
            !isFormValid &&
            touched.has('termsAndConditionsAgreed') &&
            invalid.has('termsAndConditionsAgreed');

        return {
            firstName: trans.firstName,
            lastName: trans.lastName,
            emailAddress: trans.emailAddress,
            message,
            firstNameErrorMsg: showFirstNameErrorMsg ? trans.firstNameErrorMsg : '',
            lastNameErrorMsg: showLastNameErrorMsg ? trans.lastNameErrorMsg : '',
            emailAddressErrorMsg: showEmailAddressErrorMsg ? trans.emailAddressErrorMsg : '',
            termsAndConditionsAgreedErrorMsg: showTermsAndConditionsErrorMsg
                ? trans.termsAndConditionsAgreedErrorMsg
                : '',
            phoneNumber: travelerFeatures.inquiryPhoneIsMobile
                ? trans.mobileNumber
                : trans.phoneNumber,
            question,
            countryCodes: trans.countryCodes,
            inquirySubscribeText: trans.inquirySubscribeText,
            inquiryTermsAgreementText: labelMaker(trans.inquiryTermsAgreementText, {
                brand: brandName
            })
        };
    }
);
/* eslint-enable complexity */

const getContactOwnerFormHasTripDetailsErrors = createSelector(
    [getTranslations, getContactOwnerForm, isInquiryModalOpen],
    (trans, contactOwnerForm, inquiryModalOpen) => {
        const hasInvalidGuests = !!contactOwnerForm.invalid.has('guests');

        return inquiryModalOpen ? hasInvalidGuests : null;
    }
);

const getContactOwnerFormDetails = createSelector(
    [getContactOwnerForm, getContactOwnerFormLabels, getTravelerFeatures, getUserData],
    (contactOwnerForm, contactOwnerFormLabels, travelerFeatures, userData) => {
        const {
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            newsSubscribed,
            isFormValid
        } = contactOwnerForm;
        const {termsAndConditionsRequired, showNewsCheckbox} = travelerFeatures;

        return {
            labels: contactOwnerFormLabels,
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            termsAndConditionsRequired,
            newsSubscribed,
            showNewsCheckbox,
            isFormValid,
            userData
        };
    }
);

export default getContactOwnerFormDetails;
export {
    getInquiryModalPlatformLabels,
    getContactOwnerFormLabels,
    getContactOwnerFormDetails,
    getContactOwnerFormHasTripDetailsErrors
};
